import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import {
  ConnectConfigurationKeys,
  NAMESPACE_CONNECT,
  NAMESPACE_NAVIGATION,
  NavigationConfigurationKeys,
} from '@epicuro-next/platform/configuration';
import { selectFilteredProductBasketQueryParams } from '@epicuro-next/state/p-basket-state';
import {
  initProductLists,
  selectIsOwnerOfOpenedList,
} from '@epicuro-next/state/product-list-state';
import { selectRouteData } from '@epicuro-next/state/router-store';
import { selectLookBookListIsOwned } from '@epicuro-next/state/shop-the-look-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBarcodeRead,
  faBars,
  faMagnifyingGlass,
} from '@fortawesome/pro-solid-svg-icons';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';

import { NavigationService } from './service/navigation.service';

export interface NavigationPoint {
  name: string;
  route: string;
  label: string;
  icon?: IconDefinition;
  hideDesktop?: boolean;
  appendSearchParams?: boolean;
  isActive$: Observable<boolean>;
}

const MOBILE_LISTS_PATH = `/${APP_PRIVATE_PATHS.browse.path}/${APP_PRIVATE_PATHS.mobileLists.path}`;
const SEARCH_PATH = `/${APP_PRIVATE_PATHS.browse.path}/${APP_PRIVATE_PATHS.search.path}`;
const SCAN_PATH = `/${APP_PRIVATE_PATHS.scan.path}`;
const PRODUCT_LISTS_PATH = `/${APP_PRIVATE_PATHS.browse.path}/${APP_PRIVATE_PATHS.productList.path}`;
const LOOKBOOKS_PATH = `/${APP_PRIVATE_PATHS.shopTheLook.path}`;
const HOME_PATH = `/${APP_PRIVATE_PATHS.home.path}`;

export class BaseNavigationComponent {
  /** Icon for the left menu  */
  public menuIcon = faBars;
  public faScan = faBarcodeRead;
  public faSearch = faMagnifyingGlass;

  private isOwnerOfOpenedList$ = combineLatest([
    this.store.pipe(select(selectIsOwnerOfOpenedList)),
    this.store.pipe(select(selectLookBookListIsOwned)),
  ]).pipe(
    map(([isOwnerOfOpenedList, isOwnerOfOpenedLookbook]) =>
      Boolean(isOwnerOfOpenedList || isOwnerOfOpenedLookbook),
    ),
  );

  private isOnOwnedListOrLookbook$ = combineLatest([
    this.navigationService.isRouteActive(PRODUCT_LISTS_PATH),
    this.navigationService.isRouteActive(LOOKBOOKS_PATH),
    this.isOwnerOfOpenedList$,
  ]).pipe(
    map(
      ([onLists, onALookBook, isOwner]) => (onLists || onALookBook) && isOwner,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public homeLink: NavigationPoint = {
    name: $localize`:MainNavigation|Home Nav Link@@homeNavLink:Home`,
    route: HOME_PATH,
    label: 'home',
    isActive$: this.navigationService.isRouteActive(HOME_PATH),
  };
  public mobileListsLink: NavigationPoint = {
    name: $localize`:MainNavigation|Lists Nav Link@@listsNavLink:Lists`,
    route: MOBILE_LISTS_PATH,
    label: 'lists',
    isActive$: combineLatest([
      this.navigationService.isRouteActive(MOBILE_LISTS_PATH),
      this.isOnOwnedListOrLookbook$,
    ]).pipe(map(([onLists, onOwnedList]) => onLists || onOwnedList)),
  };
  public productsLink: NavigationPoint = {
    name: $localize`:MainNavigation|Browse Nav Link@@browseNavLink:Browse`,
    route: SEARCH_PATH,
    label: 'browse',
    appendSearchParams: true,
    isActive$: this.navigationService.isRouteActive(SEARCH_PATH),
    icon: faMagnifyingGlass,
  };
  public scannerLink: NavigationPoint = {
    name: $localize`:MainNavigation|Scanner Link@@scannerNavLink:Scanner`,
    route: SCAN_PATH,
    label: 'scanner',
    appendSearchParams: false,
    isActive$: this.navigationService.isRouteActive(SCAN_PATH),
    icon: faBarcodeRead,
  };
  public myListsLink: NavigationPoint = {
    name: $localize`:MainNavigation|My List Link@@myListNavLink:My Lists`,
    route: PRODUCT_LISTS_PATH,
    label: 'my lists',
    appendSearchParams: false,
    isActive$: this.isOnOwnedListOrLookbook$,
  };

  public searchQueryParams$ = this.store.pipe(
    select(selectFilteredProductBasketQueryParams),
  );

  public connectNamespace = NAMESPACE_CONNECT;
  public connectEnabledFlag = ConnectConfigurationKeys.ConnectEnabled;
  public navigationNamespace = NAMESPACE_NAVIGATION;
  public homeAndBrowseEnabledFlag =
    NavigationConfigurationKeys.HomeAndBrowseEnabled;

  public showSearchBarOnMobile$ = this.store.pipe(
    select(selectRouteData),
    filter(isNonNullish),
    map((data) => Boolean(data.showSearchBarOnMobile)),
  );
  public isMobile$ = atxIsMobile$;

  constructor(
    protected store: Store,
    protected navigationService: NavigationService,
  ) {}

  public initProductLists() {
    this.store.dispatch(initProductLists());
  }

  public trackByRoute(_: number, navigationPoint: NavigationPoint) {
    return navigationPoint.route;
  }
}
